import React, { useState } from 'react';
import { Modal, Box, IconButton, Button, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import { AMOUNT, DISPOSITION_INPUT_TYPES, FILE_TYPES, LINK } from '../../constants/constants';
import { checkFileTypeFromUrl, formatNumberToIndianNotation } from '../../utils/commonUtils';

interface FieldItemProps {
  fieldName: string;
  value: string | number | null; // You can specify more types as needed
  fieldType?: string;
  container?:string;
  customFontSize?: string;
}

const styles: Record<string, React.CSSProperties> = {
  detailRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 0',
  },
  heading: {
    fontWeight: 'bold',
    wordWrap: 'break-word', // Allows text to break at arbitrary points if necessary
		overflowWrap: 'break-word', // Modern alternative for wrapping long words
    color: '#6e6e6e', // Medium gray
    width: '40%', // Adjust width for alignment
  },
  value: {
    fontWeight: 'bold',
    overflowWrap: 'anywhere', // Standard for breaking long strings with no spaces
    wordBreak: 'break-all',   // Fallback for older browsers
    color: '#708090',
    textAlign: 'right',
    width: '50%',
    textWrap: 'wrap'
  },
  link:{
    color: 'primary',
  },
  colon: {
    color: '#999', // Medium gray for colon
    textAlign: 'center', // Center the colon
    width: '10px', // Fixed width to ensure it centers correctly
    fontWeight: 'bold',
  },
  imageLink: {
    marginRight: 8,
    cursor: 'pointer',
  },
  imageIcon: {
    width: 50,  // or adjust to your needs
    height: 50,
    objectFit: 'cover',
    cursor: 'pointer',
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.5)',
    padding: '32px',
    maxWidth: '80%',
    maxHeight: '80%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  modalImage: {
    maxWidth: '100%',
    maxHeight: '80vh',
    objectFit: 'contain',
  },
  closeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: '#fff',
  },
  imageContainer: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end'
  },
  fileIcon: {
    objectFit: 'cover',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: 1
  },
};

const FieldItem: React.FC<FieldItemProps> = ({ fieldName, value, fieldType = "default", container, customFontSize }) => {
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleOpen = (url: string) => {
    setSelectedImage(url);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
  };

  const renderValue = (value: string | number) => {
    if ([LINK, DISPOSITION_INPUT_TYPES.file].includes(fieldType)) {
      const urls = (value as string).split(',').map(url => url.trim());
      return (
        <Box style={styles.imageContainer}>
          {urls.map((url: string, index: number) => {
            const linkType = checkFileTypeFromUrl(url);
            if (linkType === FILE_TYPES.image) {
              return (
                <img
                  key={index}
                  src={url}
                  alt={`image-${index}`}
                  style={styles.imageIcon}
                  onClick={() => handleOpen(url)}
                />
              );
            } else {
              return (
                <Button variant="outlined" key={index} sx={styles.fileIcon} onClick={() => window.open(url, '_blank')}>
                  <Typography mr={0.5} variant="subtitle2">{linkType === FILE_TYPES.pdf ? 'PDF' : 'FILE'}</Typography>
                  <FilePresentIcon fontSize="small" />
                </Button>
              );
            }
          })}
        </Box>
      );
    } else if (fieldType === AMOUNT) {
      return formatNumberToIndianNotation(Number(value));
    } else {
      return value;
    }
  };

  return (
    <div style={{ 
      ...styles.detailRow, 
      ...(container === "Additional Data" ? { borderBottom: "2px dashed #ccc" } : {}) 
    }}>
      <span style={{ ...styles.heading, ...(customFontSize ? { fontSize: customFontSize } : { fontSize: '16px' }) }}>{fieldName}</span>
      <span style={{ ...styles.colon, ...(customFontSize ? { fontSize: customFontSize } : { fontSize: '16px' }) }}>:</span>
      <span style={{ ...styles.value, ...(customFontSize ? { fontSize: customFontSize } : { fontSize: '16px' }) }}>
        {value !== null ? renderValue(value) : null}
      </span>
      
      {/* Image Modal */}
      <Modal open={open} onClose={handleClose} style={styles.modal}>
        <Box sx={styles.modalContent}>
          <IconButton sx={styles.closeButton} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
          {selectedImage && <img src={selectedImage} alt="Selected" style={styles.modalImage} />}
        </Box>
      </Modal>
    </div>
  );
};


export default FieldItem;
